// Subscription state context — fetches subscription status on wallet connect,
// provides hasFeature() helper for gating features like queue

import { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import type { ReactNode, JSX } from 'react';
import type { SubscriptionStatus } from '../api/subscription.ts';
import { fetchSubscriptionStatus } from '../api/subscription.ts';
import { useWallet } from './WalletContext.tsx';

// --- State ---

export type SubscriptionState = Readonly<{
  readonly status: SubscriptionStatus | null;
  readonly loading: boolean;
  readonly error: string | null;
}>;

const initialState: SubscriptionState = Object.freeze({
  status: null,
  loading: false,
  error: null,
});

// --- Actions ---

type SubscriptionAction =
  | Readonly<{ type: 'FETCH_START' }>
  | Readonly<{ type: 'FETCH_SUCCESS'; payload: SubscriptionStatus }>
  | Readonly<{ type: 'FETCH_ERROR'; payload: string }>
  | Readonly<{ type: 'RESET' }>;

// --- Reducer (pure) ---

const subscriptionReducer = (
  state: SubscriptionState,
  action: SubscriptionAction,
): SubscriptionState => {
  switch (action.type) {
    case 'FETCH_START':
      return Object.freeze({ ...state, loading: true, error: null });
    case 'FETCH_SUCCESS':
      return Object.freeze({ status: action.payload, loading: false, error: null });
    case 'FETCH_ERROR':
      return Object.freeze({ status: null, loading: false, error: action.payload });
    case 'RESET':
      return initialState;
  }
};

// --- Context ---

type SubscriptionContextValue = Readonly<{
  readonly state: SubscriptionState;
  readonly hasFeature: (featureType: string) => boolean;
  readonly isSubscribed: boolean;
  readonly isCancelPending: boolean;
  readonly refresh: () => void;
}>;

const defaultContextValue: SubscriptionContextValue = Object.freeze({
  state: initialState,
  hasFeature: () => false,
  isSubscribed: false,
  isCancelPending: false,
  refresh: () => {},
});

const SubscriptionCtx = createContext<SubscriptionContextValue>(defaultContextValue);

export const useSubscription = (): SubscriptionContextValue =>
  useContext(SubscriptionCtx);

// --- Provider ---

export const SubscriptionProvider = ({ children }: { readonly children: ReactNode }): JSX.Element => {
  const [state, dispatch] = useReducer(subscriptionReducer, initialState);
  const { connectedWallet, authState } = useWallet();

  const sessionId = connectedWallet?.sessionId ?? null;

  const refresh = useCallback((): void => {
    if (!sessionId || authState !== 'active') return;

    dispatch({ type: 'FETCH_START' });
    fetchSubscriptionStatus(sessionId).then(result => {
      if (result.ok) {
        dispatch({ type: 'FETCH_SUCCESS', payload: result.value });
      } else {
        dispatch({ type: 'FETCH_ERROR', payload: result.error });
      }
    });
  }, [sessionId, authState]);

  // Fetch on wallet connect / session change
  useEffect(() => {
    if (sessionId && authState === 'active') {
      refresh();
    } else {
      dispatch({ type: 'RESET' });
    }
  }, [sessionId, authState, refresh]);

  const hasFeature = useCallback((featureType: string): boolean => {
    if (!state.status?.subscribed) return false;
    return state.status.features.some(f => f.feature_type === featureType && f.disabled_at === null);
  }, [state.status]);

  const isSubscribed = state.status?.subscribed === true;
  const isCancelPending = state.status?.userCancelled === true;

  const value: SubscriptionContextValue = Object.freeze({
    state,
    hasFeature,
    isSubscribed,
    isCancelPending,
    refresh,
  });

  return (
    <SubscriptionCtx.Provider value={value}>
      {children}
    </SubscriptionCtx.Provider>
  );
};
